import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Clock, ArrowRight, ArrowLeft, X, Send, Loader2, CheckCircle } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function LeadGate({ onSubmitted }) {
  const [form, setForm] = useState({ name: "", email: "", company: "" });
  const [status, setStatus] = useState("idle");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      await axios.post(`${API}/contact`, { ...form, message: "Blog reader — lead capture", phone: "" });
      setStatus("success");
      localStorage.setItem("blog_access", "true");
      setTimeout(() => onSubmitted(), 1000);
    } catch {
      setStatus("error");
    }
  };

  const inputClass = "w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-3 text-sm text-slate-200 placeholder-slate-600 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none transition-all";

  return (
    <div className="max-w-md mx-auto text-center" data-testid="blog-lead-gate">
      <div className="w-12 h-12 bg-cyan-500/10 border border-cyan-500/20 rounded-xl flex items-center justify-center mx-auto mb-4">
        <Send className="w-5 h-5 text-cyan-400" />
      </div>
      <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Quick access — just your details</h3>
      <p className="text-sm text-slate-400 mb-6">Fill in your info to read this article and all future posts. We won't spam — just occasional AI insights.</p>

      {status === "success" ? (
        <div className="flex flex-col items-center gap-2">
          <CheckCircle className="w-8 h-8 text-emerald-400" />
          <p className="text-sm text-emerald-400 font-medium">Access granted! Opening article...</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-3" data-testid="blog-lead-form">
          <input type="text" required placeholder="Your Name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className={inputClass} data-testid="blog-lead-name" />
          <input type="email" required placeholder="Work Email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className={inputClass} data-testid="blog-lead-email" />
          <input type="text" placeholder="Company (optional)" value={form.company} onChange={e => setForm({ ...form, company: e.target.value })} className={inputClass} data-testid="blog-lead-company" />
          {status === "error" && <p className="text-xs text-red-400">Something went wrong. Try again.</p>}
          <button type="submit" disabled={status === "loading"} className="w-full bg-cyan-500 text-slate-950 py-3 rounded-full font-bold hover:bg-cyan-400 transition-all flex items-center justify-center gap-2 disabled:opacity-50" data-testid="blog-lead-submit">
            {status === "loading" ? <Loader2 size={16} className="animate-spin" /> : <>Read Article <ArrowRight size={14} /></>}
          </button>
        </form>
      )}
    </div>
  );
}

function BlogArticle({ post, onBack }) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="max-w-3xl mx-auto" data-testid="blog-article">
      <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-cyan-400 mb-8 transition-colors" data-testid="blog-back-btn">
        <ArrowLeft size={14} /> Back to all articles
      </button>
      <span className="text-xs font-bold uppercase tracking-widest text-cyan-400">{post.tag}</span>
      <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-50 mt-2 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>{post.title}</h1>
      <div className="flex items-center gap-3 text-xs text-slate-500 mb-10">
        <Clock size={12} /> <span>{post.read_time}</span> <span>·</span> <span>{post.date}</span> <span>·</span> <span>By {post.author || "Rewat Raj"}</span>
      </div>
      <div className="space-y-5">
        {(post.content || []).map((block, i) =>
          block.type === "h" ? (
            <h2 key={i} className="text-xl font-semibold text-slate-100 mt-8 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{block.text}</h2>
          ) : (
            <p key={i} className="text-base text-slate-400 leading-relaxed">{block.text}</p>
          )
        )}
      </div>
      <div className="mt-12 p-6 rounded-2xl bg-cyan-500/5 border border-cyan-500/20 text-center">
        <p className="text-sm text-slate-300 mb-3">Want to explore how AI can work for your business?</p>
        <a href="#contact" onClick={onBack} className="inline-flex items-center gap-2 bg-cyan-500 text-slate-950 px-6 py-2.5 rounded-full text-sm font-bold hover:bg-cyan-400 transition-all">
          Book Free Discovery Call <ArrowRight size={14} />
        </a>
      </div>
    </motion.div>
  );
}

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPost, setSelectedPost] = useState(null);
  const [showGate, setShowGate] = useState(null);

  useEffect(() => {
    axios.get(`${API}/blog/posts`)
      .then(res => setPosts(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const hasAccess = () => localStorage.getItem("blog_access") === "true";

  const handleCardClick = (post) => {
    if (hasAccess()) {
      setSelectedPost(post);
      setTimeout(() => document.getElementById("blog")?.scrollIntoView({ behavior: "smooth" }), 100);
    } else {
      setShowGate(post);
    }
  };

  const handleGateSubmitted = () => {
    setSelectedPost(showGate);
    setShowGate(null);
    setTimeout(() => document.getElementById("blog")?.scrollIntoView({ behavior: "smooth" }), 100);
  };

  return (
    <section id="blog" data-testid="blog-section" className="py-24 md:py-32 relative bg-slate-900/30">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <AnimatePresence mode="wait">
          {selectedPost ? (
            <BlogArticle key="article" post={selectedPost} onBack={() => setSelectedPost(null)} />
          ) : (
            <motion.div key="list" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-16 gap-4">
                <div>
                  <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-400 mb-4">
                    Insights & Perspectives
                  </motion.p>
                  <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-50">
                    Latest from our <span className="gradient-text">Blog</span>
                  </motion.h2>
                </div>
              </div>

              {/* Lead gate modal */}
              <AnimatePresence>
                {showGate && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-sm px-6" onClick={() => setShowGate(null)}>
                    <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 20 }} className="glass-card rounded-2xl p-8 max-w-md w-full relative" onClick={e => e.stopPropagation()}>
                      <button onClick={() => setShowGate(null)} className="absolute top-4 right-4 text-slate-500 hover:text-white transition-colors" data-testid="blog-gate-close">
                        <X size={18} />
                      </button>
                      <LeadGate onSubmitted={handleGateSubmitted} />
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>

              {loading ? (
                <div className="flex justify-center py-20">
                  <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
                </div>
              ) : posts.length === 0 ? (
                <p className="text-center text-slate-500 py-20">No blog posts yet.</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {posts.map((post, i) => (
                    <motion.article
                      key={post.slug || post.id || i}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.1 }}
                      className="group cursor-pointer"
                      onClick={() => handleCardClick(post)}
                      data-testid={`blog-card-${i}`}
                    >
                      <div className="glass-card rounded-2xl p-6 h-full hover:-translate-y-1 transition-all duration-500 hover:border-cyan-500/30 flex flex-col">
                        <span className="text-xs font-bold uppercase tracking-widest text-cyan-400 mb-4">{post.tag}</span>
                        <h3 className="text-lg font-semibold tracking-tight text-slate-100 mb-3 group-hover:text-cyan-300 transition-colors">{post.title}</h3>
                        <p className="text-sm text-slate-400 leading-relaxed mb-6 flex-1">{post.excerpt}</p>
                        <div className="flex items-center justify-between pt-4 border-t border-slate-800">
                          <div className="flex items-center gap-2 text-xs text-slate-500">
                            <Clock size={12} /> <span>{post.read_time}</span> <span className="mx-1">·</span> <span>{post.date}</span>
                          </div>
                          <span className="text-xs text-cyan-400 font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                            Read <ArrowRight size={12} />
                          </span>
                        </div>
                      </div>
                    </motion.article>
                  ))}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
